import "./Pricing.css";
import SiteHeader, { SiteHeaderAccountActions } from "./SiteHeader";
import { PLANS, FEATURES, PRICING_NOTES, CONTACT, formatPrice } from "./plans";

// État d'une case du tableau. La pastille terracotta signale l'offre qui
// *introduit* la fonctionnalité, la verte celles qui en héritent : on voit
// ainsi d'un coup d'œil ce que chaque palier apporte de plus. Les
// fonctionnalités communes à toutes les offres restent vertes partout — il n'y
// a pas de nouveauté à y signaler.
function cellState(feature, planId) {
  if (!feature.plans.includes(planId)) return "no";
  const isBase = feature.plans.length === PLANS.length;
  const introducedBy = PLANS.find((p) => feature.plans.includes(p.id));
  return !isBase && introducedBy.id === planId ? "new" : "yes";
}

function IncludedIcon({ state }) {
  if (state === "no") {
    return (
      <span className="pricing-compare-dash" role="img" aria-label="Non inclus">
        —
      </span>
    );
  }
  return (
    <span
      className={`pricing-compare-icon is-${state}`}
      role="img"
      aria-label={state === "new" ? "Inclus, nouveau dans cette formule" : "Inclus"}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

// Met en gras le mot signalé par `strong` sans casser le libellé en plusieurs
// champs (le libellé complet reste utilisable comme clé et pour la recherche).
function FeatureLabel({ feature }) {
  if (!feature.strong) return feature.label;
  const [before, ...after] = feature.label.split(feature.strong);
  return (
    <>
      {before}
      <strong>{feature.strong}</strong>
      {after.join(feature.strong)}
    </>
  );
}

function PriceBlock({ label, period, price, priceFull, note, badge, onChoose, dark }) {
  return (
    <div className="pricing-price-block">
      <span className="pricing-price-label">{label}</span>
      <div className="pricing-price-line">
        <span className="pricing-price-amount">{formatPrice(price)} €</span>
        <span className="pricing-price-period">{period}</span>
        {priceFull && (
          <span className="rc-strike pricing-price-full">
            {formatPrice(priceFull)} €
          </span>
        )}
      </div>
      {badge && (
        <span className={`rc-badge ${dark ? "" : "rc-badge-soft"} pricing-price-badge`}>
          {badge}
        </span>
      )}
      {note && <p className="pricing-price-note">{note}</p>}
      <button
        className={`rc-btn ${dark ? "rc-btn-cream" : "rc-btn-primary"} pricing-choose-btn`}
        onClick={onChoose}
      >
        Choisir cette formule
      </button>
    </div>
  );
}

function Pricing({ user, onBack, onSelectPlan, onGoLogin, onGoGestion, onLogout, onMenuNavigate }) {
  return (
    <div className="pricing">
      {/* Même header que la landing : marque + connexion/gestion, où qu'on soit */}
      <SiteHeader onLogoClick={onBack} onBack={onBack}>
        <SiteHeaderAccountActions
          user={user}
          onGoLogin={onGoLogin}
          onGoGestion={onGoGestion}
          onLogout={onLogout}
          onMenuNavigate={onMenuNavigate}
        />
      </SiteHeader>

      <section className="pricing-hero">
        <p className="rc-eyebrow">
          Nos offres · Sans engagement caché, un prix clair
        </p>
        <h1>
          Plantez votre programme de fidélité,
          <br />
          <em>et faites revenir vos clients.</em>
        </h1>
        <p className="pricing-hero-sub">
          Une carte de fidélité qui vit dans le téléphone de vos clients — sans
          application, sans carte physique perdue. Choisissez votre formule, on
          s'occupe du reste.
        </p>
      </section>

      <section className="pricing-plans-section" id="plans">
        <div className="pricing-inner">
          {PLANS.map((plan) => (
            <article
              key={plan.id}
              className={`pricing-offer${plan.featured ? " pricing-offer-featured" : ""}`}
            >
              {plan.featured && (
                <span className="rc-badge pricing-offer-flag">
                  {plan.featuredBadge}
                </span>
              )}
              <div className="pricing-offer-head">
                <h2 className="pricing-offer-name">{plan.name}</h2>
                <span className="rc-offer-num">
                  {plan.num} · {plan.nickname}
                </span>
              </div>
              <p className="pricing-offer-desc">{plan.description}</p>
              <div className="pricing-offer-prices">
                <PriceBlock
                  label="Mensuel"
                  period="/ mois"
                  price={plan.monthly}
                  priceFull={plan.monthlyFull}
                  note={plan.setupNote}
                  dark={plan.featured}
                  onChoose={() => onSelectPlan({ planId: plan.id, billing: "monthly" })}
                />
                <div className="pricing-price-divider" />
                <PriceBlock
                  label="Annuel"
                  period="/ an"
                  price={plan.annual}
                  priceFull={plan.annualFull}
                  badge={plan.annualBadge}
                  dark={plan.featured}
                  onChoose={() => onSelectPlan({ planId: plan.id, billing: "annual" })}
                />
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="pricing-compare-section">
        <div className="pricing-inner">
          <h2 className="pricing-compare-title">Comparer les offres</h2>
          <p className="pricing-compare-sub">
            Toutes les formules incluent la carte de fidélité complète — les
            suivantes ajoutent la visibilité, puis la communication.
          </p>
          <div className="pricing-compare-scroll">
            <table className="pricing-compare-table">
              <thead>
                <tr>
                  <th scope="col" className="pricing-compare-feature-col">
                    Fonctionnalités
                  </th>
                  {PLANS.map((plan) => (
                    <th
                      key={plan.id}
                      scope="col"
                      className={plan.featured ? "is-featured" : ""}
                    >
                      <span className="pricing-compare-brand">Fidelity Card</span>
                      <span className="pricing-compare-plan">{plan.tier}</span>
                      <span className="pricing-compare-price">
                        {formatPrice(plan.monthly)} € / mois
                      </span>
                      <span className="pricing-compare-price-annual">
                        {formatPrice(plan.annual)} € / an
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((feature) => (
                  <tr key={feature.label}>
                    <th scope="row">
                      <FeatureLabel feature={feature} />
                    </th>
                    {PLANS.map((plan) => (
                      <td
                        key={plan.id}
                        className={plan.featured ? "is-featured" : ""}
                      >
                        <IncludedIcon state={cellState(feature, plan.id)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pricing-extra-panel">
            <span className="rc-badge pricing-extra-badge">Exclusivité Extra</span>
            <h3 className="pricing-extra-title">
              Vos relances tournent toutes seules.
            </h3>
            <p className="pricing-extra-text">
              Vous choisissez le message, la situation qui le déclenche et le
              moment : un SMS de bienvenue à l'inscription, une offre de −10 %
              trois jours plus tard, une relance aux clients qui ne sont pas
              revenus depuis six semaines. Vous le configurez une fois, il part
              ensuite tout seul, au nom de votre commerce.
            </p>
          </div>

          <div className="pricing-notes">
            {PRICING_NOTES.map((note) => (
              <div className="pricing-note" key={note.title}>
                <h4 className="pricing-note-title">{note.title}</h4>
                <p className="pricing-note-text">{note.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pricing-contact">
        <div className="pricing-inner pricing-contact-inner">
          <div>
            <h2 className="pricing-contact-title">Plantons votre projet.</h2>
            <p className="pricing-contact-sub">
              Racontez-nous votre commerce — réponse sous 24 h, avec des idées
              concrètes et un prix clair.
            </p>
          </div>
          <div className="pricing-contact-links">
            <a href={`tel:${CONTACT.phone.replace(/ /g, "")}`}>{CONTACT.phone}</a>
            <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
            <a href={`https://${CONTACT.site}`} target="_blank" rel="noopener noreferrer">
              {CONTACT.site}
            </a>
          </div>
        </div>
      </section>

      <footer className="pricing-footer">
        <div className="pricing-footer-inner">
          <span className="rc-wordmark pricing-footer-brand">Racines</span>
          <span className="pricing-footer-copy">
            © {new Date().getFullYear()} Racines · Offres 2026
          </span>
        </div>
      </footer>
    </div>
  );
}

export default Pricing;
